import {useCallback, useEffect} from 'react';
import {useLazyQuery} from '@apollo/client';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import getCountriesCostYearsQuery from '@/Pages/RegionalCosts/graphql/getCountriesCostYears';
import {CountryCostYear} from '../store/types';
import {countriesCostYearsFetched} from '../store/slice';

interface UseCountryCostYearsResult {
  countriesCostYears: CountryCostYear[] | null;
  refetchCountriesCostYears: () => Promise<void>;
}

export const useCountriesCostYears = (): UseCountryCostYearsResult => {
  const countriesCostYears = useAppSelector(
    ({regionalCosts}) => regionalCosts.countriesCostYears
  );
  const dispatch = useAppDispatch();

  const [fetchCountriesCostYears] = useLazyQuery<{
    countriesCostYears: CountryCostYear[];
  }>(getCountriesCostYearsQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache'
  });

  const getCountriesCostYears = useCallback(
    async (force = false) => {
      if (!force && countriesCostYears) return;

      try {
        const {data, error} = await fetchCountriesCostYears();

        if (error) {
          const errorMessage =
            error.message ?? 'Failed to fetch countries cost years.';
          console.error(errorMessage);
          return;
        }

        dispatch(countriesCostYearsFetched(data?.countriesCostYears ?? []));
      } catch (err) {
        console.error(
          `Unexpected error while fetching countries cost years: ${err}`
        );
      }
    },
    [countriesCostYears, dispatch, fetchCountriesCostYears]
  );

  useEffect(() => {
    getCountriesCostYears();
  }, []); // eslint-disable-line

  const refetchCountriesCostYears = useCallback(
    () => getCountriesCostYears(true),
    [getCountriesCostYears]
  );

  return {countriesCostYears, refetchCountriesCostYears};
};
